/* __next_internal_client_entry_do_not_use__ createIntersectionObserver auto */ import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState, createContext, useContext } from "react";
/**
 * Creates a pair of Provider, Consumer for IntersectionObserver that can be used to observe elements in the DOM.
 * This pattern is particularly useful when you want to fire the same callback for multiple elements.
 * Instead of creating a new IntersectionObserver for each element, you can use the instance created by the Provider.
 * @example
 * ```tsx
 * // TrackProductTile.tsx
 * const { usePairedIntersectionObserver, IntersectionObserverProvider } =
 *   createIntersectionObserver();
 * export const TrackProductTileProvider = ({ children }) => {
 *   return (
 *     <IntersectionObserverProvider
 *       callback={() => {
 *         // fire tracking event
 *        }}
 *      >
 *        {children}
 *      </IntersectionObserverProvider>
 *    );
 * };
 *
 * export const useTrackProductTile = () => {
 *   return usePairedIntersectionObserver<HTMLDivElement>();
 * };
 *
 * // SearchPage.tsx
 * const SearchPage = () => {
 *   return (
 *     <TrackProductTileProvider>
 *       // Search Page Content
 *     </TrackProductTileProvider>
 *   )
 * }
 *
 * // ProductTile.tsx
 * const ProductTile = () => {
 *   const elementRef = useTrackProductTile();
 *   return (
 *     <div ref={elementRef}>
 *        // Product Tile Content
 *     </div>
 *   )
 * }
 * ```
 */ export var createIntersectionObserver = function() {
    var Context = /*#__PURE__*/ createContext(undefined);
    var IntersectionObserverProvider = function(param) {
        var callback = param.callback, _param_options = param.options, options = _param_options === void 0 ? {} : _param_options, children = param.children;
        var rootMargin = options.rootMargin, root = options.root, threshold = options.threshold;
        var callbackRef = useRef(callback);
        callbackRef.current = callback;
        var _useState = _sliced_to_array(useState(), 2), observer = _useState[0], setObserver = _useState[1];
        useEffect(function() {
            var observerInstance = new IntersectionObserver(function() {
                for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
                    args[_key] = arguments[_key];
                }
                var _callbackRef;
                (_callbackRef = callbackRef).current.apply(_callbackRef, _to_consumable_array(args));
            }, {
                rootMargin: rootMargin,
                root: root,
                threshold: threshold
            });
            setObserver(observerInstance);
            return function() {
                observerInstance === null || observerInstance === void 0 ? void 0 : observerInstance.disconnect();
                setObserver(undefined);
            };
        }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            rootMargin,
            root
        ]);
        return /*#__PURE__*/ _jsx(Context.Provider, {
            value: observer,
            children: children
        });
    };
    var usePairedIntersectionObserver = function() {
        var observer = useContext(Context);
        var _useState = _sliced_to_array(useState(null), 2), element = _useState[0], setElement = _useState[1];
        useEffect(function() {
            var elementRef = element;
            if (elementRef) {
                observer === null || observer === void 0 ? void 0 : observer.observe(element);
            }
            return function() {
                if (elementRef) {
                    observer === null || observer === void 0 ? void 0 : observer.unobserve(elementRef);
                }
            };
        }, [
            element,
            observer
        ]);
        return useCallback(function(e) {
            setElement(e);
        }, []);
    };
    return {
        IntersectionObserverProvider: IntersectionObserverProvider,
        usePairedIntersectionObserver: usePairedIntersectionObserver
    };
};
